import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { t } from "@/i18n";

const TONES = {
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  blue: "bg-sky-50 text-sky-700 ring-sky-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  red: "bg-red-50 text-red-700 ring-red-200",
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  teal: "bg-teal-50 text-teal-800 ring-teal-200",
  navy: "bg-[#0A2540]/5 text-[#0A2540] ring-[#0A2540]/15",
};

const STATUS_TONE = {
  trial: "blue", active: "green", past_due: "amber", suspended: "red", cancelled: "slate",
  pending: "amber", accepted: "green", earned: "green", used: "slate", rejected: "red", change_requested: "blue", open: "blue", done: "green", dismissed: "slate", revoked: "slate", expired: "slate", invited: "blue", none: "slate",
  pending_verification: "amber", garage_manager: "navy", mechanic: "teal", customer: "slate", support: "amber",
  live: "green", offline: "slate", fixed: "navy", indicative: "teal", on_quote: "amber", ended: "slate",
  simulated: "amber", complete: "green", incomplete: "amber", inactive: "slate", archived: "slate", confirmed: "green",
  awaiting_parts: "amber", hold: "slate", declined: "red", work: "navy", dropoff: "teal", auto: "green", validation: "amber", proposed: "blue", emergency: "red", internal: "slate",
  booked: "slate", received: "blue", inspecting: "blue", awaiting_approval: "amber", approved: "teal", in_progress: "navy", ready: "green", completed: "slate",
  draft: "slate", sent: "amber", superseded: "slate", checked_in: "teal", part: "slate", labour: "slate", other: "slate", before: "slate", after: "slate",
};

export function Badge({ tone = "slate", children, testId }) {
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${TONES[tone]}`}>
      {children}
    </span>
  );
}

export function StatusBadge({ status, testId }) {
  return <Badge tone={STATUS_TONE[status] || "slate"} testId={testId}>{t(`status.${status}`)}</Badge>;
}

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between gf-rise">
      <div>
        {eyebrow && <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">{eyebrow}</p>}
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function LoadingState({ label, full }) {
  return (
    <div data-testid="loading-state" className={`flex items-center justify-center gap-3 text-slate-500 ${full ? "min-h-screen" : "py-16"}`}>
      <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
      <span className="text-sm">{label || t("common.loading")}</span>
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div data-testid="error-state" className="gf-card flex flex-col items-start gap-3 border-red-200 p-6">
      <div className="flex items-center gap-2 text-red-700">
        <AlertTriangle className="h-5 w-5" />
        <p className="font-semibold">{t("common.errorTitle")}</p>
      </div>
      <p className="text-sm text-slate-600">{message}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} data-testid="error-retry-button">
          <RefreshCw className="mr-2 h-4 w-4" /> {t("common.retry")}
        </Button>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, action, testId = "empty-state" }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
      {Icon && <div className="mb-4 rounded-full bg-teal-50 p-3"><Icon className="h-6 w-6 text-teal-700" /></div>}
      <p className="font-display text-lg font-bold text-slate-900">{title}</p>
      {text && <p className="mt-1 max-w-md text-sm text-slate-600">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Field({ label, htmlFor, hint, error, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && <Label htmlFor={htmlFor} className="text-sm font-medium text-slate-800">{label}</Label>}
      {children}
      {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
      {error && <p className="text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
}

export function FormError({ message }) {
  if (!message) return null;
  return (
    <div data-testid="form-error" role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{message}</span>
    </div>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, onConfirm, destructive, busy, testId = "confirm-dialog" }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testId}-confirm`}
            disabled={busy}
            onClick={(e) => { e.preventDefault(); onConfirm(); }}
            className={destructive ? "bg-red-600 hover:bg-red-700" : ""}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel || t("common.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function Card({ children, className = "", testId }) {
  return <div data-testid={testId} className={`gf-card p-6 ${className}`}>{children}</div>;
}

export function SectionTitle({ children, hint }) {
  return (
    <div className="mb-5">
      <h2 className="text-lg font-bold text-slate-900">{children}</h2>
      {hint && <p className="mt-1 text-sm text-slate-600">{hint}</p>}
    </div>
  );
}

export function SubmitButton({ busy, children, ...props }) {
  return (
    <Button type="submit" disabled={busy || props.disabled} {...props}>
      {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {children}
    </Button>
  );
}

export function StatCard({ label, value, icon: Icon, testId, hint }) {
  return (
    <div data-testid={testId} className="gf-card p-6 transition-transform duration-200 hover:-translate-y-0.5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
        {Icon && <Icon className="h-5 w-5 text-teal-600" />}
      </div>
      <p className="mt-3 font-display text-4xl font-extrabold text-slate-900" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}
